import { arrow, autoUpdate, computePosition, flip, limitShift, offset, shift, size } from '@floating-ui/dom';

import { buildPropsHelper } from './helpers/props';

export default {
  Popover: {
    ...buildPropsHelper(),

    mounted() {
      this.isShown = undefined;
      this.handleShow = this.show.bind(this);
      this.handleHide = this.hide.bind(this);

      this.initialize();

      if (this.openOnMount) {
        this.show({ target: null });
      }
    },

    updated() {
      if (this.isShown && this.contentElement.classList.contains('hidden')) {
        this.contentElement.classList.remove('hidden');
      }

      if (this.propsChanged() || this.isShown) {
        this.removeAllEventListeners();
        this.initialize();
      }
    },

    destroyed() {
      this.cleanup();
    },

    getProps() {
      return {
        toggleElementId: this.el.dataset.popoverToggleId,
        contentElementId: this.el.dataset.popoverContentId,
        arrowElementId: this.el.dataset.popoverContentId,
        popoverArrowEnabled: this.el.dataset.popoverArrow === 'true',
        popoverInteractive: this.el.dataset.popoverInteractive === 'true',
        popoverTrigger: this.el.dataset.popoverTrigger,
        popoverOffset: parseInt(this.el.dataset.popoverOffset),
        popoverMaxHeight: this.el.dataset.popoverMaxHeight,
        popoverPlacement: this.el.dataset.popoverPlacement,
        isShown: document.getElementById(this.el.dataset.popoverContentId).classList.contains('hidden') ? false : true,
        padding: 5,
        onShowEvent: this.el.dataset.popoverOnShowEvent,
        onHideEvent: this.el.dataset.popoverOnHideEvent,
        openOnMount: this.el.dataset.popoverOpenOnMount === 'true',
        eventTarget: this.el.dataset.popoverEventTarget,
      };
    },

    initialize() {
      this.setProps();

      this.toggleElement = document.getElementById(this.el.dataset.popoverToggleId);
      this.contentElement = document.getElementById(this.el.dataset.popoverContentId);
      this.arrowElement = document.getElementById(this.el.dataset.popoverContentId).querySelector('.popover-arrow');

      let options = {
        placement: this.popoverPlacement,
        strategy: 'absolute',
        middleware: [offset(this.popoverOffset), shift({ padding: this.padding, limiter: limitShift() })],
      };

      if (this.popoverMaxHeight === 'available') {
        const contentEl = this.contentElement;

        options.middleware.push(
          size({
            apply({ availableHeight }) {
              Object.assign(contentEl.style, {
                maxHeight: `${availableHeight}px`,
              });
            },
            padding: this.padding,
          }),
        );
      } else {
        options.middleware.push(flip());
      }

      if (this.popoverArrowEnabled) options.middleware.push(arrow({ element: this.arrowElement }));

      this.autoUpdateCleanup = autoUpdate(this.el, this.contentElement, () => {
        computePosition(this.el, this.contentElement, options).then(({ x, y, placement, middlewareData }) => {
          Object.assign(this.contentElement.style, {
            left: `${x}px`,
            top: `${y}px`,
          });

          if (this.popoverArrowEnabled) {
            const { x: arrowX, y: arrowY } = middlewareData.arrow;

            const staticSide = {
              top: 'bottom',
              right: 'left',
              bottom: 'top',
              left: 'right',
            }[placement.split('-')[0]];

            Object.assign(this.arrowElement.style, {
              left: arrowX != null ? `${arrowX}px` : '',
              top: arrowY != null ? `${arrowY}px` : '',
              right: '',
              bottom: '',
              [staticSide]: '-3px',
            });
          }
        });
      });

      this.addShowEventListeners();

      if (this.isShown) {
        this.addHideEventListeners();
        this.el.classList.add('popover-shown');
      }
    },

    cleanup() {
      this.isShown = undefined;
      this.autoUpdateCleanup();
      this.removeAllEventListeners();
    },

    addShowEventListeners() {
      setTimeout(() => {
        if (this.popoverTrigger === 'click') {
          this.toggleElement.addEventListener('click', this.handleShow);
        }

        if (this.popoverTrigger === 'hover') {
          this.toggleElement.addEventListener('mouseenter', this.handleShow);
          this.toggleElement.addEventListener('focus', this.handleShow);
        }
      }, 0);
    },

    addHideEventListeners() {
      setTimeout(() => {
        if (this.popoverTrigger === 'click') {
          document.addEventListener('click', this.handleHide);
        }

        if (this.popoverTrigger === 'hover') {
          document.addEventListener('mousemove', this.handleHide);
        }

        this.el.addEventListener('close', this.handleHide);
      }, 0);
    },

    removeHideEventListeners() {
      if (this.popoverTrigger === 'click') {
        document.removeEventListener('click', this.handleHide);
      }

      if (this.popoverTrigger === 'hover') {
        document.removeEventListener('mousemove', this.handleHide);
      }

      this.el.removeEventListener('close', this.handleHide);
    },

    removeAllEventListeners() {
      if (this.popoverTrigger === 'click') {
        this.toggleElement.removeEventListener('click', this.handleShow);
      }

      if (this.popoverTrigger === 'hover') {
        this.toggleElement.removeEventListener('mouseenter', this.handleShow);
        this.toggleElement.removeEventListener('focus', this.handleShow);
      }

      this.removeHideEventListeners();
    },

    show(event) {
      if (this.isShown && this.popoverTrigger === 'click') return this.hide();

      this.addHideEventListeners();

      this.isShown = true;
      this.contentElement.classList.remove('hidden');
      this.el.classList.add('popover-shown');

      if (this.onShowEvent) {
        if (this.eventTarget) this.pushEventTo(this.eventTarget, this.onShowEvent);
        else this.pushEvent(this.onShowEvent);
      }

      return true;
    },

    hide(event) {
      if (event === undefined || this.shouldHide(event)) {
        this.isShown = false;

        this.contentElement.classList.add('hidden');
        this.el.classList.remove('popover-shown');

        this.removeHideEventListeners();

        if (this.onHideEvent) {
          if (this.eventTarget) this.pushEventTo(this.eventTarget, this.onHideEvent);
          else this.pushEvent(this.onHideEvent);
        }
      }
    },

    shouldHide(event) {
      const eventThreshold = this.popoverOffset + 2;
      const overToggle = this.distanceFromEl(this.toggleElement, event) <= eventThreshold;
      const overContent = this.distanceFromEl(this.contentElement, event) <= eventThreshold;
      const withinContent = this.contentElement.contains(event.target);

      switch (this.popoverTrigger) {
        case 'click':
          return !(this.popoverInteractive && (overContent || withinContent));

        case 'hover':
          if (this.popoverInteractive) {
            return !(overToggle || overContent || withinContent);
          } else {
            return !overToggle;
          }
      }
    },

    distanceFromEl(el, event) {
      const x = event.clientX;
      const y = event.clientY;

      const rect = el.getBoundingClientRect();

      return Math.max(rect.top - y, y - rect.bottom, rect.left - x, x - rect.right);
    },
  },
};
